import { ThemeProvider, Theme } from "@mui/material/styles";
import { memo, ReactNode, useEffect, useLayoutEffect } from "react";
import GlobalStyles from "@mui/material/GlobalStyles";
import { Box, CssBaseline } from "@mui/material";

/**
 * A função useEnhancedEffect usa condicionalmente o hook useLayoutEffect quando o objeto window está definido.
 * Caso contrário, usa o hook useEffect.
 */
const useEnhancedEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

interface FuseThemeProps {
	children: ReactNode;
	theme: Theme;
	root?: boolean;
}

/**
 * O componente FuseTheme é responsável por renderizar o ThemeProvider do MUI com o tema e a direção informados.
 * Também define a direção do documento e adiciona uma classe no body com base no mode do tema.
 * O componente é memoizado para evitar re-renders desnecessários.
 */
function FuseTheme(props: FuseThemeProps) {
	const { theme, children, root = false } = props;
	const langDirection = theme.direction;

	useEnhancedEffect(() => {
		if (root) {
			document.documentElement.dir = langDirection;
		}
	}, [langDirection]);

	const baseStyles = {
		"& .MuiOutlinedInput-root": {
			backgroundColor: theme.vars.palette.common.background,
		},
		"& .border-divider ": {
			borderColor: `${theme.vars.palette.divider}!important`,
		},
		"table.simple tbody tr th": {
			borderColor: theme.vars.palette.divider,
		},
		"table.simple thead tr th": {
			borderColor: theme.vars.palette.divider,
		},
		"a.link, a:not([role=button])[target=_blank]": {
			backgroundColor: `rgba(${theme.vars.palette.secondary.mainChannel} / 0.2)`,
			color: "inherit",
			borderBottom: `1px solid ${theme.vars.palette.divider}`,
			textDecoration: "none",
			"&:hover": {
				backgroundColor: `rgba(${theme.vars.palette.secondary.mainChannel} / 0.3)`,
				textDecoration: "none",
			},
		},
		/*
		 * Keep divider as fallback without overriding explicit Tailwind border colors.
		 * :where() keeps specificity at 0, so utilities like border-danger/30 still win.
		 */
		':where([class^="border"])': {
			borderColor: theme.vars.palette.divider,
		},
		':where([class*="border"])': {
			borderColor: theme.vars.palette.divider,
		},
		':where([class*="divide-"]) > :not([hidden])': {
			borderColor: theme.vars.palette.divider,
		},
		hr: {
			borderColor: theme.vars.palette.divider,
		},
		"::-webkit-scrollbar-thumb": {
			boxShadow: `inset 0 0 0 20px ${theme.palette.mode === "light" ? "rgba(0, 0, 0, 0.24)" : "rgba(255, 255, 255, 0.24)"}`,
		},
		"::-webkit-scrollbar-thumb:active": {
			boxShadow: `inset 0 0 0 20px ${theme.palette.mode === "light" ? "rgba(0, 0, 0, 0.37)" : "rgba(255, 255, 255, 0.37)"}`,
		},
	};
	// console.warn('FuseTheme:: rendered',mainTheme);
	return (
		<ThemeProvider theme={theme}>
			{root ? <CssBaseline /> : null}
			<Box
				sx={{
					...(root
						? {
								display: "flex",
								flexDirection: "column",
								width: "100%",
								minHeight: "100vh",
							}
						: baseStyles),
				}}
			>
				{children}
			</Box>
			{root && (
				<GlobalStyles
					styles={(theme) => ({
						html: {
							backgroundColor: `${theme.vars.palette.background.default}`,
							color: `${theme.vars.palette.text.primary}`,
							fontFamily: theme.typography.fontFamily,
						},
						body: {
							backgroundColor: theme.vars.palette.background.default,
							color: theme.vars.palette.text.primary,
							fontFamily: theme.typography.fontFamily,
						},
						...baseStyles,
					})}
				/>
			)}
		</ThemeProvider>
	);
}

export default memo(FuseTheme);
